import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Repeat, CalendarCheck, Wallet, Banknote, Loader2, CheckCircle2, PauseCircle, PlayCircle, XCircle, Search } from "lucide-react";
import api from "@/lib/api";
import { formatJMD, PAYMENT_LABEL, fmtDate } from "@/lib/format";
import { Navbar } from "@/components/public/Navbar";
import { Footer } from "@/components/public/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const FREQ_LABEL = { weekly: "Every week", biweekly: "Every 2 weeks" };
export const SUB_STATUS = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  paused: "bg-amber-100 text-amber-800 border-amber-200",
  cancelled: "bg-rose-100 text-rose-700 border-rose-200",
};

const empty = { customer_name: "", phone: "", email: "", address: "", product_id: "", quantity: 1, frequency: "weekly", weekday: "0", payment_method: "cod", notes: "" };

const ManagePanel = ({ initialCode }) => {
  const [code, setCode] = useState(initialCode || "");
  const [phone, setPhone] = useState("");
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!code || !phone) return toast.error("Enter your standing order code and phone");
    setBusy(true);
    try {
      const { data } = await api.get("/subscriptions/lookup", { params: { code, phone } });
      setSub(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Not found"); setSub(null); } finally { setBusy(false); }
  };

  const act = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/subscriptions/${sub.id}/customer-action`, { action, phone });
      setSub(data);
      toast.success(`Standing order ${data.status}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white/10 cw-glass-dark rounded-3xl p-6 border border-sky-300/20" data-testid="subscription-manage">
      <h3 className="font-display text-lg font-bold text-white flex items-center gap-2"><Search className="w-5 h-5 text-sky-300" /> Manage an existing plan</h3>
      <form onSubmit={lookup} className="mt-4 grid grid-cols-2 gap-3">
        <Input data-testid="sub-manage-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="SUB-1234" className="h-10 rounded-xl bg-white/90 font-mono-cw uppercase" />
        <Input data-testid="sub-manage-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" className="h-10 rounded-xl bg-white/90" />
        <Button type="submit" data-testid="sub-manage-lookup" disabled={busy} className="col-span-2 h-10 rounded-xl bg-sky-400 hover:bg-sky-300 text-sky-950 font-bold">Find my plan</Button>
      </form>
      {sub && (
        <div className="mt-5 rounded-2xl bg-white p-4 text-sm text-slate-700 space-y-2" data-testid="sub-manage-result">
          <div className="flex items-center justify-between">
            <span className="font-mono-cw font-bold text-sky-700">{sub.code}</span>
            <span data-testid="sub-manage-status" className={`px-2.5 py-0.5 rounded-full border text-xs font-bold capitalize ${SUB_STATUS[sub.status]}`}>{sub.status}</span>
          </div>
          <div><strong>{sub.quantity}× {sub.product_name}</strong> · {FREQ_LABEL[sub.frequency]} on {WEEKDAYS[sub.weekday]}s</div>
          <div>{formatJMD(sub.unit_price * sub.quantity)} per delivery · {PAYMENT_LABEL[sub.payment_method]}</div>
          {sub.status === "active" && <div className="text-sky-700">Next delivery: <strong>{fmtDate(sub.next_delivery)}</strong></div>}
          <div className="flex gap-2 pt-2">
            {sub.status === "active" && <Button size="sm" variant="outline" disabled={busy} onClick={() => act("pause")} data-testid="sub-manage-pause" className="rounded-lg"><PauseCircle className="w-4 h-4 mr-1" /> Pause</Button>}
            {sub.status === "paused" && <Button size="sm" disabled={busy} onClick={() => act("resume")} data-testid="sub-manage-resume" className="rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white"><PlayCircle className="w-4 h-4 mr-1" /> Resume</Button>}
            {sub.status !== "cancelled" && <Button size="sm" variant="ghost" disabled={busy} onClick={() => act("cancel")} data-testid="sub-manage-cancel" className="rounded-lg text-rose-600 ml-auto"><XCircle className="w-4 h-4 mr-1" /> Cancel plan</Button>}
          </div>
        </div>
      )}
    </div>
  );
};

export default function SubscribePage() {
  const [params] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ ...empty, product_id: params.get("product") || "" });
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.stock_status === "in_stock"))).catch(() => {});
  }, []);

  const refills = useMemo(() => products.filter((p) => p.type === "refill"), [products]);
  const others = useMemo(() => products.filter((p) => p.type !== "refill"), [products]);
  const chosen = products.find((p) => p.id === form.product_id);
  const perDelivery = chosen ? chosen.price_jmd * Number(form.quantity || 0) : 0;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.phone || !form.address || !form.product_id) return toast.error("Please fill in name, phone, address and choose a product.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/subscriptions", { ...form, quantity: Number(form.quantity), weekday: Number(form.weekday), email: form.email || null });
      setCreated(data);
      toast.success("Standing order created!");
      setForm(empty);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create standing order"); } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-sky-950 min-h-screen" data-testid="subscribe-page">
      <Navbar solid />
      <section className="relative pt-32 pb-24 cw-ocean-gradient overflow-hidden">
        <div className="absolute -top-10 left-1/4 w-96 h-96 bg-cyan-400 rounded-full cw-blob opacity-30" />
        <div className="max-w-6xl mx-auto px-5 lg:px-8 relative">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-300">Standing Orders</span>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white mt-3">Never run dry. <span className="cw-text-gradient">Set it and forget it.</span></h1>
            <p className="mt-4 text-sky-100/80">Pick your refill, choose a day, and we'll automatically schedule a delivery every week or every two weeks. Pause or cancel anytime with your code.</p>
          </div>

          <div className="mt-12 grid lg:grid-cols-5 gap-6">
            <motion.form onSubmit={submit} data-testid="subscription-form" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-3 bg-white rounded-3xl p-7 lg:p-9 shadow-2xl space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Full Name</Label><Input data-testid="sub-input-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="e.g. Marcia Brown" className="h-11 rounded-xl" /></div>
                <div className="space-y-1.5"><Label>Phone / WhatsApp</Label><Input data-testid="sub-input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="876-555-0100" className="h-11 rounded-xl" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Email <span className="text-slate-400 font-normal">(optional)</span></Label><Input type="email" data-testid="sub-input-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className="h-11 rounded-xl" /></div>
                <div className="space-y-1.5"><Label>Delivery Address & Parish</Label><Input data-testid="sub-input-address" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Street, landmark, parish" className="h-11 rounded-xl" /></div>
              </div>
              <div className="grid sm:grid-cols-[1fr_120px] gap-4">
                <div className="space-y-1.5">
                  <Label>Product</Label>
                  <Select value={form.product_id} onValueChange={(v) => set("product_id", v)}>
                    <SelectTrigger data-testid="sub-select-product" className="h-11 rounded-xl"><SelectValue placeholder="Choose a refill or bottle" /></SelectTrigger>
                    <SelectContent>
                      {refills.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatJMD(p.price_jmd)}</SelectItem>)}
                      {others.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatJMD(p.price_jmd)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Quantity</Label><Input type="number" min={1} data-testid="sub-input-quantity" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="h-11 rounded-xl" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>How often</Label>
                  <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
                    <SelectTrigger data-testid="sub-select-frequency" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="weekly">Every week</SelectItem><SelectItem value="biweekly">Every 2 weeks</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Delivery day</Label>
                  <Select value={form.weekday} onValueChange={(v) => set("weekday", v)}>
                    <SelectTrigger data-testid="sub-select-weekday" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Payment each delivery</Label>
                <div className="grid grid-cols-2 gap-3">
                  {[{ k: "cod", label: "Cash on Delivery", icon: Wallet, t: "sub-payment-cod" }, { k: "bank_transfer", label: "Bank Transfer", icon: Banknote, t: "sub-payment-bank-transfer" }].map((pm) => (
                    <button type="button" key={pm.k} data-testid={pm.t} onClick={() => set("payment_method", pm.k)} className={`flex items-center gap-2 h-12 rounded-xl border-2 px-4 font-semibold text-sm transition-all ${form.payment_method === pm.k ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                      <pm.icon className="w-4 h-4" /> {pm.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5"><Label>Notes (optional)</Label><Textarea data-testid="sub-input-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="rounded-xl resize-none" placeholder="Gate code, landmarks…" /></div>
              <Button type="submit" data-testid="sub-submit" disabled={submitting} className="w-full py-6 rounded-xl text-base font-bold bg-sky-600 hover:bg-sky-500 text-white">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Repeat className="w-5 h-5 mr-2" /> Start standing order · {formatJMD(perDelivery)} / delivery</>}
              </Button>
            </motion.form>

            <div className="lg:col-span-2 space-y-5">
              {created && (
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} data-testid="subscription-confirmation" className="bg-white rounded-3xl p-6 shadow-2xl">
                  <div className="flex items-center gap-3"><CheckCircle2 className="w-9 h-9 text-emerald-500" /><div><h3 className="font-display text-xl font-bold text-slate-900">You're all set!</h3><p className="text-xs text-slate-500">Save this code to manage your plan</p></div></div>
                  <div className="mt-4 font-mono-cw text-3xl font-extrabold text-sky-700" data-testid="subscription-code">{created.code}</div>
                  <div className="mt-3 text-sm text-slate-700 space-y-1">
                    <div><strong>{created.quantity}× {created.product_name}</strong></div>
                    <div>{FREQ_LABEL[created.frequency]} on {WEEKDAYS[created.weekday]}s</div>
                    <div className="flex items-center gap-1.5 text-sky-700"><CalendarCheck className="w-4 h-4" /> First delivery: <strong>{fmtDate(created.next_delivery)}</strong></div>
                  </div>
                </motion.div>
              )}
              <div className="bg-white/10 cw-glass-dark rounded-3xl p-6 border border-sky-300/20 text-sky-100/90 text-sm space-y-3">
                <div className="flex items-center gap-2 text-sky-200 font-semibold text-xs uppercase tracking-wider"><Repeat className="w-4 h-4" /> How it works</div>
                <p>1. Choose your product, quantity, day and frequency.</p>
                <p>2. Each delivery is created automatically the morning of your day and our driver brings it to your door.</p>
                <p>3. Pay the driver in cash or by bank transfer — same as any order.</p>
                <p>4. Pause while you travel, or cancel anytime using your code.</p>
                {chosen && <div className="border-t border-sky-300/20 pt-3 flex justify-between items-center"><span className="font-semibold text-white">Per delivery</span><span className="font-mono-cw text-2xl font-extrabold text-sky-300">{formatJMD(perDelivery)}</span></div>}
              </div>
              <ManagePanel initialCode={params.get("code") || ""} />
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
